import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { formatWithOrdinal } from '../../../utils/dateHelpers';
import GreetingHeader from '../../shared/GreetingHeader';
import LoadingDots from '../../shared/LoadingDots';
import { fetchQueue, selectQueue, selectQueueLoading } from '../../../ReduxSlices/Patients/patientQueueSlice';
import { selectAppointments, fetchAppointments, selectAppointmentsLoading } from '../../../ReduxSlices/Appointments/appointmentsSlice';

const DoctorDashboard = ({ user, navigateTo }) => {
  const dispatch = useDispatch();
  // Redux state
  const queue = useSelector(selectQueue);
  const queueLoading = useSelector(selectQueueLoading);
  const appointments = useSelector(selectAppointments);
  const appointmentsLoading = useSelector(selectAppointmentsLoading);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchAppointments());
    dispatch(fetchQueue());
  }, [dispatch]);


  const todayDate = new Date().toISOString().split('T')[0];

  const todayAppointments = appointments.filter((appt) => appt.scheduled_at.startsWith(todayDate));

  // console.log(
  // 'QUEUE DEBUG:',
  // queue.map((entry) => ({
  //   id: entry.id,
  //   active: entry.active,
  //   status: entry.status,
  //   intent: entry.intent,
  //   passesActive: entry.active === true,
  //   passesStatus:
  //     entry.status === 'waiting' ||
  //     entry.status === 'waiting_for_lab_results',
  //   passesIntent: entry.intent !== 'lab',
  // }))
// );

  // Count patients in the queue with status "waiting"
const waitingPatientsCount = queue.filter(
  (entry) =>
    ['waiting', 'waiting_for_lab_results'].includes(entry.status) &&
    entry.intent !== 'lab'
).length;


  return (
    <div className="p-6">
      <GreetingHeader name={user?.name} />
      
          {/* Dashboard Grid */}
      <div className="labdashboard grid grid-cols-4 sm:grid-cols-2 gap-6">

        {/* Quick Actions */}
        <div className="quick-actions pending col-span-2 col-start-1 mt-6">
          <h3 className="text-lg mx-auto font-semibold mb-3">Quick Actions</h3>
          <div className="flex gap-[10px] mb-3">
            <button
              type="button"
              onClick={() => navigateTo('calendar_app')}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
            >
              📅 View All Appointments
            </button>
            <button
              type="button"
              onClick={() => navigateTo('today_queue')}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded-lg shadow"
            >
              🧑‍⚕️ View Today’s Patient Queue
            </button>
          </div>
          {/* Display waiting patients count */}
          { queueLoading ? (
               <LoadingDots height={10} width={30} color="#38AD3E" />
          ) : (
            <p className="text-lg font-semibold mx-auto text-red-600">
              🕒 Waiting Patients in Queue:
              {' '}
              <strong>{waitingPatientsCount}</strong>
            </p>
          )}
        </div>

        {/* Today's Appointments Preview */}
        <div className="pending col-span-2 mt-10 items-center bg-white border rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 ">📋 Today’s Appointments</h3>
          {(() => {
            if (appointmentsLoading) {
                return <LoadingDots height={10} width={30} color="#38AD3E" />;
            }

            if (todayAppointments.length === 0) {
              return <p className="text-gray-500 mx-auto">No appointments scheduled for today.</p>;
            }

            return (
              <ul className="divide-y">
                {todayAppointments.map((appt) => (
                  <li key={appt.id} className="py-2">
                    <p>
                      <strong>{appt.patient?.name}</strong>
                      {' '}
                      –
                      {' '}
                      <span>
                        {formatWithOrdinal(appt.scheduled_at)}
                        {' '}
                        –
                        {' '}
                      </span>
                      <span className="capitalize">{appt.status}</span>
                    </p>
                    {appt.notes && (
                      <p className="text-sm text-gray-500 mt-1 italic">{appt.notes}</p>
                    )}
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

DoctorDashboard.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  navigateTo: PropTypes.func.isRequired,
};

export default DoctorDashboard;
